/**
 * TriStateResponseControl (DQGMS · P-001 / P-002)
 *
 * The new gate response pattern — replaces the binary "problem found" checkbox
 * with three mutually-exclusive buttons:
 *   OK      → check passes, no further data captured
 *   REWORK  → defect type / location / rework type / photo required
 *   KO      → defect type / location / photo required + Line Leader approval
 *
 * Props:
 *   value           — one of 'OK' | 'REWORK' | 'KO' | null (unset)
 *   onChange        — (nextValue) => void
 *   details         — { defect_type_id, defect_location_id, rework_type_id, note }
 *   onDetailsChange — (patch) => void
 *   lookups         — { defect_types, defect_locations, rework_types }
 *   gateType        — 'QG1' | 'QG2' | 'QG_FAN' — if 'QG_FAN', KO button is hidden
 *   language        — 'en' | 'it' — controls label field used from lookup rows
 *
 * Standalone: no coupling to any parent component's internal state shape.
 * The mockup wires this to the existing ChecklistPage row data model via a
 * thin adapter — see ChecklistPage.js after the P-001 change.
 */
import React from 'react';

const COLORS = {
  OK:     { bg: '#22a06b', bgSoft: '#e6f6ee', text: '#0f5a3a' },
  REWORK: { bg: '#d4661f', bgSoft: '#fdf0e0', text: '#8a4610' },
  KO:     { bg: '#d94a3a', bgSoft: '#fce8e6', text: '#8a2c22' },
};

const label = (row, language) => (language === 'it' ? row.label_it : row.label_en) || row.label_en;

function Button({ mode, value, onChange, disabled }) {
  const active = value === mode;
  const c = COLORS[mode];
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(mode)}
      style={{
        flex: 1,
        minHeight: 44,
        padding: '10px 12px',
        border: `2px solid ${active ? c.bg : '#d6dde5'}`,
        background: active ? c.bg : '#fff',
        color: active ? '#fff' : c.text,
        fontWeight: 700,
        fontSize: 14,
        letterSpacing: 0.4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderRadius: 8,
        boxShadow: active ? `0 2px 6px ${c.bg}55` : 'none',
        transition: 'background 0.12s ease, box-shadow 0.12s ease',
      }}
      aria-pressed={active}
    >
      {mode}
    </button>
  );
}

function Select({ options, value, onChange, placeholder, language, required }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      required={required}
      style={{
        width: '100%',
        minHeight: 40,
        padding: '8px 10px',
        border: '1px solid #cfd6de',
        borderRadius: 6,
        background: '#fff',
        fontSize: 13,
        color: '#17253d',
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.id} value={opt.id}>{label(opt, language)}</option>
      ))}
    </select>
  );
}

export default function TriStateResponseControl({
  value,
  onChange,
  details = {},
  onDetailsChange,
  lookups = { defect_types: [], defect_locations: [], rework_types: [] },
  gateType = 'QG1',
  language = 'en',
  disabled = false,
  // 'full' = all three buttons (legacy demo)
  // 'defect' = REWORK + KO only (default per revised UX — OK is implicit via the leftmost row checkbox)
  // 'npd' = a single "No Problem Detected" OK-style button, no defect fields
  variant = 'defect',
}) {
  const showKO = gateType !== 'QG_FAN';
  const showOK = variant === 'full' || variant === 'npd';
  const showRework = variant === 'full' || variant === 'defect';
  const showKOBtn = (variant === 'full' || variant === 'defect') && showKO;

  const patch = (next) => onDetailsChange && onDetailsChange(next);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Segmented control */}
      <div style={{ display: 'flex', gap: 8 }}>
        {showOK     && <Button mode="OK"     value={value} onChange={onChange} disabled={disabled} />}
        {showRework && <Button mode="REWORK" value={value} onChange={onChange} disabled={disabled} />}
        {showKOBtn  && <Button mode="KO"     value={value} onChange={onChange} disabled={disabled} />}
      </div>

      {/* Conditional panel */}
      {value === 'REWORK' && (
        <div
          style={{
            padding: 12,
            background: COLORS.REWORK.bgSoft,
            border: `1px solid ${COLORS.REWORK.bg}55`,
            borderRadius: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ fontWeight: 700, color: COLORS.REWORK.text, fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Rework details
          </div>
          <Select
            options={lookups.defect_types}
            value={details.defect_type_id}
            onChange={(v) => patch({ defect_type_id: v })}
            placeholder={language === 'it' ? 'Tipo di difetto…' : 'Defect type…'}
            language={language}
            required
          />
          <Select
            options={lookups.defect_locations}
            value={details.defect_location_id}
            onChange={(v) => patch({ defect_location_id: v })}
            placeholder={language === 'it' ? 'Posizione difetto…' : 'Defect location…'}
            language={language}
            required
          />
          <Select
            options={lookups.rework_types}
            value={details.rework_type_id}
            onChange={(v) => patch({ rework_type_id: v })}
            placeholder={language === 'it' ? 'Tipo di rework…' : 'Rework type…'}
            language={language}
            required
          />
          <div style={{ fontSize: 12, color: COLORS.REWORK.text }}>
            📷 Photo required — capture from checklist row (existing camera flow).
          </div>
        </div>
      )}

      {value === 'KO' && showKO && (
        <div
          style={{
            padding: 12,
            background: COLORS.KO.bgSoft,
            border: `1px solid ${COLORS.KO.bg}55`,
            borderRadius: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ fontWeight: 700, color: COLORS.KO.text, fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Knock-Out details — Line Leader approval required
          </div>
          <Select
            options={lookups.defect_types}
            value={details.defect_type_id}
            onChange={(v) => patch({ defect_type_id: v })}
            placeholder={language === 'it' ? 'Tipo di difetto…' : 'Defect type…'}
            language={language}
            required
          />
          <Select
            options={lookups.defect_locations}
            value={details.defect_location_id}
            onChange={(v) => patch({ defect_location_id: v })}
            placeholder={language === 'it' ? 'Posizione difetto…' : 'Defect location…'}
            language={language}
            required
          />
          <div style={{ fontSize: 12, color: COLORS.KO.text }}>
            📷 Photo required. On submit, this KO is auto-assigned an ID
            (e.g. <code>CH123456-QG1-01</code>) and a Line Leader PIN is
            requested before it is committed.
          </div>
        </div>
      )}

      {value === 'OK' && (
        <div style={{ fontSize: 12, color: COLORS.OK.text, padding: '4px 2px' }}>
          ✓ No action required. Operator authorises on final submit.
        </div>
      )}
    </div>
  );
}
